// ============================================================================
// timeline.js — the geometry behind every Time and Wage Audit timeline.
//
// One implementation, shared by the shift list and the per-driver drill-down.
// Both draw the same thing on the same axis, and the arithmetic is subtle
// enough (Pacific, midnight-crossing, outlier clamping) that two copies would
// drift the first time either was touched.
//
// ── Everything here is Pacific ───────────────────────────────────────────────
// Positions are elapsed minutes from a shift's own anchor, offset by that
// anchor's Pacific minute-of-day. That is deliberate rather than converting
// each instant independently: shifts routinely cross midnight (a punch-out at
// 00:03 UTC is 17:03 the previous Pacific day), and elapsed-time arithmetic
// from one anchor cannot produce a bar that runs backwards. It also means the
// axis needs no special case for the DST change.
//
// The underlying instants carry mixed offsets by origin — Workday punches and
// yard arrival as +00:00, Fleetio inspections as the real Pacific offset — so
// everything goes through Date parsing, never string slicing.
// ============================================================================

/** Minute-of-day (0–1439) of an instant, read in Pacific. */
export function ptMinuteOfDay(iso) {
  if (!iso) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(iso));
  const h = Number(parts.find((p) => p.type === 'hour')?.value);
  const m = Number(parts.find((p) => p.type === 'minute')?.value);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
}

/** Elapsed minutes between two instants — sign preserved. */
export function minutesBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return null;
  return (new Date(toIso).getTime() - new Date(fromIso).getTime()) / 60000;
}

/**
 * Axis tick label, in 12-hour Pacific time.
 *
 * 12-hour because everyone reading this page reads shift times that way, and
 * an axis that says 17:00 over a list of times that say 5:04 PM makes the
 * reader convert on every glance.
 *
 * The hour is bare when the tick lands on it — "6 AM", not "6:00 AM" — which
 * is the normal case, since axisTicks steps in whole hours. Off-hour ticks
 * keep their minutes rather than rounding, because a label that disagrees with
 * the mark under it is worse than a longer label.
 */
export function hhmm(mins) {
  const m = ((Math.round(mins) % 1440) + 1440) % 1440;
  const h24 = Math.floor(m / 60);
  const min = m % 60;
  const suffix = h24 < 12 ? 'AM' : 'PM';
  // 0 and 12 both read as 12 — midnight is 12 AM, noon is 12 PM.
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return min === 0 ? `${h12} ${suffix}` : `${h12}:${String(min).padStart(2, '0')} ${suffix}`;
}

export function fmtMin(n) {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  return Number.isFinite(v) ? `${v.toFixed(1)} min` : '—';
}

export function fmtHours(mins) {
  if (mins === null || mins === undefined) return '—';
  const v = Number(mins);
  return Number.isFinite(v) ? `${(v / 60).toFixed(1)} h` : '—';
}

/**
 * Resolve one shift to axis positions.
 *
 * Accepts either shape the API returns: a row from /shifts (flat, with a
 * `meals` array) or a day from /timeline/:id (a `post_trip` object plus
 * `meals`). Normalising here rather than at two call sites is the point of
 * this module.
 */
export function layoutShift(row) {
  const pt = row.post_trip !== undefined ? row.post_trip : row;
  const meals = row.meals || [];
  const hasRecord = row.post_trip !== undefined
    ? !!row.post_trip
    : row.has_shift_record !== false;

  // A meal-only day has no punches, so anchor on the first gap instead. The
  // row still renders — it just has no shift envelope to draw.
  const anchorIso = (hasRecord && pt?.shift_start) || meals[0]?.gap_start || null;
  if (!anchorIso) return null;

  const base = ptMinuteOfDay(anchorIso);
  if (base === null) return null;
  const at = (iso) => {
    const d = minutesBetween(anchorIso, iso);
    return d === null ? null : base + d;
  };

  return {
    date: String(row.shift_date || row.date || '').slice(0, 10),
    hasRecord,
    shiftStart: hasRecord && pt?.shift_start ? base : null,
    shiftEnd:   hasRecord ? at(pt?.shift_end) : null,
    workedMinutes: hasRecord ? pt?.worked_minutes ?? null : null,
    yardArrival: hasRecord ? at(pt?.yard_arrival) : null,
    inspEnd:     hasRecord ? at(pt?.inspection_submitted) : null,
    // ── Pre-trip: the same shift's other end (047) ─────────────────────────
    // clock-in -> pre-trip inspection -> yard departure, mirroring
    // arrival -> inspection -> punch-out. Positions only; whether they are
    // worth drawing is the renderer's call, since 95.6% of rows carry no
    // pre-trip reading at all and an empty marker is worse than none.
    preInspStart:  hasRecord ? at(pt?.pre_inspection_started) : null,
    preInspEnd:    hasRecord ? at(pt?.pre_inspection_submitted) : null,
    yardDeparture: hasRecord ? at(pt?.yard_departure) : null,
    // ── Site visits (049) ──────────────────────────────────────────────────
    // Where the driver actually was during the shift. Positioned like
    // everything else; whether a visit is worth drawing is the renderer's
    // call. A visit with no `departed` is one the producer saw a driver enter
    // and not leave — it keeps its arrival position and renders as a point
    // rather than a span, because inventing an end would claim an observation
    // that was never made.
    visits: (row.visits || [])
      .map((v) => ({ ...v, from: at(v.arrived), to: at(v.departed) }))
      .filter((v) => v.from !== null),
    meals: meals
      .map((m) => ({ ...m, from: at(m.gap_start), to: at(m.gap_end) }))
      .filter((m) => m.from !== null),
    post_trip: hasRecord ? pt : null,
    raw: row,
  };
}

/**
 * Axis bounds across a set of laid-out shifts, padded to whole hours.
 *
 * Inspection timestamps are deliberately excluded from the bounds. They are
 * the one field with known-bad outliers — a form_left_open row carries a
 * 1,027-minute "inspection", a form opened and submitted seventeen hours
 * apart. Letting those set the scale stretches the axis to a full 24 hours and
 * squashes every real shift into a few pixels: one bad row destroying the
 * readability of every good one. Markers outside the window are clamped by the
 * renderer instead.
 */
export function axisBounds(laid, windowAxis) {
  // A window-wide axis from the server wins, and is the normal case. Deriving
  // bounds from the fifty rows on screen made the scale rescale on every page
  // -- 4 September ran 02:08-20:47 on page one and 00:03-22:47 on page three,
  // so a bar of one width meant two different durations and the grid moved
  // under the reader as they paged. A shared axis whose meaning changes is
  // worse than an honest one that is slightly too wide.
  //
  // The server's band is trimmed (see AXIS_TRIM_QUANTILE), so a handful of
  // marks legitimately fall outside it. The renderer pins those to the edge
  // and flags them; widening to fit would put the 30-hour untrimmed span back
  // and squash every ordinary shift.
  if (windowAxis
      && Number.isFinite(windowAxis.lo) && Number.isFinite(windowAxis.hi)
      && windowAxis.hi > windowAxis.lo) {
    return {
      axisFrom: Math.floor(windowAxis.lo / 60) * 60 - 30,
      axisTo:   Math.ceil(windowAxis.hi / 60) * 60 + 30,
    };
  }

  let lo = Infinity, hi = -Infinity;
  for (const d of laid) {
    // yardDeparture joins the bounds; the pre-trip INSPECTION timestamps do
    // not, for the same reason the post-trip ones are excluded above. They
    // carry the same known-bad tail — a form_left_open row in the profiled
    // export sits 640 minutes before its own clock-in, and letting that set
    // the scale would squash every real shift into a few pixels.
    // Visits DO set the axis, unlike the inspection timestamps above. They
    // carry no known-bad tail -- a geofence entry and exit are both observed
    // events, where a "1,027-minute inspection" is a form somebody left open
    // -- and a driver's last site of the day is routinely the widest real
    // thing on the row. Excluding them would clip the very marks this feed
    // was added to show.
    for (const v of axisMarks(d)) { lo = Math.min(lo, v); hi = Math.max(hi, v); }
  }
  if (!Number.isFinite(lo)) return { axisFrom: 5 * 60, axisTo: 20 * 60 };
  return { axisFrom: Math.floor(lo / 60) * 60 - 30, axisTo: Math.ceil(hi / 60) * 60 + 30 };
}

/**
 * Every mark that sits on the axis for one laid-out shift.
 *
 * Shared with axisBounds so "what the axis covers" and "what the renderer
 * checks for overflow" cannot drift into different lists -- a mark counted by
 * one and not the other is either an unexplained clipped row or a silently
 * hidden one.
 */
export function axisMarks(d) {
  return [d.shiftStart, d.shiftEnd, d.yardArrival, d.yardDeparture,
          ...(d.visits || []).flatMap((x) => [x.from, x.to]),
          ...d.meals.flatMap((m) => [m.from, m.to])]
    .filter((v) => v !== null && v !== undefined && Number.isFinite(v));
}

/** Hour ticks at a density that suits the span. */
export function axisTicks(axisFrom, axisTo) {
  const span = Math.max(axisTo - axisFrom, 60);
  const step = span > 900 ? 180 : span > 480 ? 120 : 60;
  const out = [];
  for (let m = Math.ceil(axisFrom / step) * step; m <= axisTo; m += step) out.push(m);
  return out;
}

/** A meal gap the source system calls unambiguous. */
export function isClearGap(meal) {
  return Number(meal?.pct_moving) >= 90;
}

// ── Site visit colour ────────────────────────────────────────────────────────
// Visits sit side by side on one row and are compared with each other, so this
// needs the ALL-PAIRS validated set, not the 7-slot categorical one — see the
// chart-colour warning in CLAUDE.md. coVisible has four slots and there are
// five site types, so the four that carry a real distinction take them and
// `other` takes the neutral. That is the documented way past the ceiling:
// fold the tail into chart.other rather than generating a fifth hue that has
// not been tested against the others.
export const SITE_TYPE_COLOR = {
  company_yard:         0,   // index into chart.coVisible
  company_landfill:     1,
  third_party_disposal: 2,
  fueling:              3,
};

export function visitColor(chart, siteType) {
  const i = SITE_TYPE_COLOR[siteType];
  return i === undefined ? chart.other : chart.coVisible[i];
}

export function siteTypeLabel(siteType) {
  return String(siteType || 'other').replace(/_/g, ' ');
}
